import { gql } from '@apollo/client';
import { apolloClient } from '../config/apollo-client';

export interface ChildBalanceReport {
  cashCashIn: number;
  cashCashOut: number;
  cashProfit: number;
  cryptoCashIn: number;
  cryptoCashOut: number;
  cryptoProfit: number;
  totalCashIn: number;
  totalCashOut: number;
  totalProfit: number;
}

export interface GetChildBalanceReportInput {
  userId: number;
  startDate?: string; // ISO date string, e.g. "2026-06-03T00:00"
  endDate?: string; // ISO date string, e.g. "2026-06-10T23:59"
  environmentID?: number | null;
}

export const EMPTY_CHILD_BALANCE_REPORT: ChildBalanceReport = {
  cashCashIn: 0,
  cashCashOut: 0,
  cashProfit: 0,
  cryptoCashIn: 0,
  cryptoCashOut: 0,
  cryptoProfit: 0,
  totalCashIn: 0,
  totalCashOut: 0,
  totalProfit: 0,
};

// Variables are declared as scalars (instead of a named input type) so this stays
// in sync with the backend even if the input object type name changes.
// The environmentID parts are included only when an id is provided so the field
// is omitted entirely (rather than sent as null) when it's not set.
const buildQuery = (includeEnvironmentID: boolean) => gql`
  query GetChildBalanceReport(
    $userId: Int!
    $startDate: DateTime
    $endDate: DateTime
    ${includeEnvironmentID ? '$environmentID: Int' : ''}
  ) {
    getChildBalanceReport(
      input: {
        userId: $userId
        startDate: $startDate
        endDate: $endDate
        ${includeEnvironmentID ? 'environmentID: $environmentID' : ''}
      }
    ) {
      cashCashIn
      cashCashOut
      cashProfit
      cryptoCashIn
      cryptoCashOut
      cryptoProfit
      totalCashIn
      totalCashOut
      totalProfit
    }
  }
`;

export const getChildBalanceReport = async (
  input: GetChildBalanceReportInput,
): Promise<ChildBalanceReport> => {
  try {
    const includeEnvironmentID =
      input.environmentID !== null && input.environmentID !== undefined;

    const { data } = await apolloClient.query<{
      getChildBalanceReport: ChildBalanceReport;
    }>({
      query: buildQuery(includeEnvironmentID),
      variables: {
        userId: input.userId,
        startDate: input.startDate,
        endDate: input.endDate,
        ...(includeEnvironmentID
          ? { environmentID: input.environmentID }
          : {}),
      },
      fetchPolicy: 'network-only',
    });

    return data?.getChildBalanceReport ?? EMPTY_CHILD_BALANCE_REPORT;
  } catch (error) {
    console.error('Error fetching child balance report:', error);
    throw error;
  }
};
